<template>
  <div class="radial-progress">
    <div class="radial-progress__container">
      <svg class="radial-progress__svg" viewBox="0 0 150 150">
        <circle class="progress-bg" cx="75" cy="75" r="69"></circle>
        <circle
          class="progress-bar"
          :style="progressBarStyle"
          cx="75"
          cy="75"
          r="69"
        ></circle>
      </svg>
      <div
        v-if="isProgressInside"
        class="radial-progress__info"
        :aria-label="`${progressDisplay} ${label}`"
      >
        <div
          class="radial-progress__info-progress radial-progress__info-progress--small"
        >
          {{ progressDisplay }}
        </div>
      </div>
      <slot name="icon"></slot>
    </div>
    <div
      v-if="!isProgressInside"
      aria-live="polite"
      class="radial-progress__info"
    >
      <div class="radial-progress__info-progress">{{ progressDisplay }}</div>
      <div class="radial-progress__info-label">{{ label }}</div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, useSlots } from 'vue'
import type { TranslateResult } from 'vue-i18n'

export type RadialProgress = { value: number; max: number }

const props = withDefaults(
  defineProps<{
    progress: RadialProgress
    label?: string | TranslateResult
    progressType?: 'percentage' | 'fraction'
    color?: string
  }>(),
  {
    progress: () => ({ value: 10, max: 100 }),
    label: '',
    progressType: 'percentage',
    color: 'currentColor',
  }
)

const slots = useSlots()
const isProgressInside = computed(() => slots.icon === undefined)

const progressRatio = computed(() => {
  if (!props.progress || props.progress.max === 0) return 0
  return props.progress.value / props.progress.max
})

const progressDisplay = computed(() => {
  if (props.progressType === 'percentage') {
    const ratio = props.progress.value / props.progress.max || 0
    return Math.round(ratio * 100) + '%'
  } else {
    return `${props.progress.value}/${props.progress?.max || 0}`
  }
})

const strokeDashOffset = computed(() => {
  const circumference = 2 * Math.PI * 69 // Circumference of the circle with radius 69
  return circumference * (1 - progressRatio.value)
})

const progressBarStyle = computed(() => ({
  strokeDashoffset: strokeDashOffset.value,
  stroke: props.color,
}))
</script>

<style lang="scss" scoped>
.radial-progress {
  display: flex;
  gap: $spacing-3xs;
  align-items: center;
  height: 100%;
  min-width: 130px;

  &__container {
    position: relative;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__svg {
    position: absolute;
    width: 100%;
    height: 100%;
    transform: rotate(-90deg); /* Starts progress from the top */
    .progress {
      &-bg {
        fill: none;
        stroke: $color-ash;
        stroke-width: 12;
      }

      &-bar {
        fill: none;
        stroke-width: 12;
        stroke-dasharray: calc(
          2 * pi * 69
        ); /* Circumference of a 69px radius circle (2 * pi * 69) */
        transition: stroke-dashoffset 0.5s ease-in-out;
      }
    }
  }

  &__info {
    display: flex;
    padding: $spacing-5xs 0;
    flex-direction: column;
    align-items: flex-start;
    color: $color-text-light;
    &-progress {
      @include title1();
      &--small {
        font-size: $font-size-xs;
      }
    }
    &-label {
      font-size: $font-size-s;
      font-weight: 700;
      color: $color-text-light;
    }
  }
}
</style>
